import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { confirmSignUp, signInWithRedirect, signUp } from 'aws-amplify/auth';
import { isAmplifyAuthConfigured } from '../config/amplifyAuth';
import {
  recordErrorStage,
  recordFunnelStep,
  recordLatency,
  recordPageView,
  recordUserAction,
} from '../utils/rum';
import { buildOnboardingSearch, persistV2OnboardingState, resolveV2OnboardingState } from '../utils/v2Onboarding';
import { hasOnboardingState, onboardingRumContext, recordV2FunnelStep } from '../utils/v2Rum';

const SignUp: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const hasTrackedPageViewRef = useRef(false);
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [confirmationCode, setConfirmationCode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [requiresConfirmation, setRequiresConfirmation] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [showPasswordPolicyHint, setShowPasswordPolicyHint] = useState(false);
  const onboardingState = resolveV2OnboardingState(location.search);
  const nextSearch = buildOnboardingSearch(onboardingState);
  const isPendingWorkspaceCreate = Boolean(onboardingState.pendingCreate);

  useEffect(() => {
    if (hasTrackedPageViewRef.current) {
      return;
    }
    hasTrackedPageViewRef.current = true;
    recordPageView('signup_page', {
      route: '/signup',
      ...onboardingRumContext(onboardingState),
    });
    if (hasOnboardingState(onboardingState)) {
      recordV2FunnelStep('v2_onboarding_auth_resume', 'shown', onboardingRumContext(onboardingState));
    }
  }, [onboardingState]);

  const getFriendlyError = (authError: any): string => {
    const code = authError?.name || authError?.code;

    switch (code) {
      case 'UsernameExistsException':
        return 'An account with this email already exists.';
      case 'InvalidPasswordException':
        return 'Password does not meet the required complexity requirements.';
      case 'CodeMismatchException':
        return 'Invalid verification code. Please try again.';
      case 'ExpiredCodeException':
        return 'Verification code expired. Request a new one from sign up.';
      case 'TooManyRequestsException':
      case 'LimitExceededException':
        return 'Too many attempts. Please wait and try again.';
      default:
        return authError?.message || 'Something went wrong. Please try again.';
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    const startedAt = performance.now();
    setError('');
    setInfo('');
    setShowPasswordPolicyHint(false);
    recordFunnelStep('signup_submit', 'start', {
      email: email.trim(),
      ...onboardingRumContext(onboardingState),
    });

    if (!isAmplifyAuthConfigured) {
      setError('Authentication is not configured yet.');
      recordFunnelStep('signup_submit', 'blocked', {
        reason: 'auth_not_configured',
      });
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      recordFunnelStep('signup_submit', 'blocked', {
        reason: 'password_mismatch',
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await signUp({
        username: email.trim(),
        password,
        options: {
          userAttributes: {
            name: fullName.trim(),
            email: email.trim(),
          },
        },
      });

      if (result.nextStep.signUpStep === 'CONFIRM_SIGN_UP') {
        setRequiresConfirmation(true);
        setInfo('Verification code sent to your email. Enter it below to complete sign up.');
        recordFunnelStep('signup_submit', 'success', {
          requires_confirmation: true,
          ...onboardingRumContext(onboardingState),
        });
      } else {
        setInfo('Account created successfully. You can sign in now.');
        recordFunnelStep('signup_submit', 'success', {
          requires_confirmation: false,
          ...onboardingRumContext(onboardingState),
        });
        setTimeout(() => navigate(`/login${nextSearch}`), 800);
      }
      recordLatency('signup_submit', performance.now() - startedAt);
    } catch (authError: any) {
      const code = authError?.name || authError?.code;
      setShowPasswordPolicyHint(code === 'InvalidPasswordException');
      setError(getFriendlyError(authError));
      recordFunnelStep('signup_submit', 'failure', {
        error_code: code || 'unknown',
      });
      recordErrorStage('signup_submit', authError);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoogleSignUp = async () => {
    setError('');
    setInfo('');
    recordFunnelStep('google_signup_redirect', 'start');

    if (!isAmplifyAuthConfigured) {
      setError('Authentication is not configured yet.');
      recordFunnelStep('google_signup_redirect', 'blocked', {
        reason: 'auth_not_configured',
      });
      return;
    }

    setIsGoogleLoading(true);
    try {
      persistV2OnboardingState(onboardingState);
      await signInWithRedirect({ provider: 'Google' });
    } catch (authError: any) {
      setError(getFriendlyError(authError));
      setIsGoogleLoading(false);
      recordFunnelStep('google_signup_redirect', 'failure');
      recordErrorStage('google_signup_redirect', authError);
    }
  };

  const handleConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    const startedAt = performance.now();
    setError('');
    setInfo('');
    recordFunnelStep('signup_confirmation', 'start', {
      email: email.trim(),
    });

    if (!email.trim()) {
      setError('Email is required.');
      recordFunnelStep('signup_confirmation', 'blocked', {
        reason: 'missing_email',
      });
      return;
    }

    setIsSubmitting(true);
    try {
      await confirmSignUp({
        username: email.trim(),
        confirmationCode: confirmationCode.trim(),
      });
      setInfo('Email verified. You can sign in now.');
      recordFunnelStep('signup_confirmation', 'success', {
        email: email.trim(),
      });
      recordLatency('signup_confirmation', performance.now() - startedAt);
      setTimeout(() => navigate(`/login${nextSearch}`), 800);
    } catch (authError: any) {
      setError(getFriendlyError(authError));
      recordFunnelStep('signup_confirmation', 'failure', {
        error_code: authError?.name || authError?.code || 'unknown',
      });
      recordErrorStage('signup_confirmation', authError);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
      <div className="min-h-screen bg-white text-[#101830]">
        <div className="grid min-h-screen lg:grid-cols-[560px_minmax(0,1fr)]">
          <aside className="flex flex-col justify-center bg-[#101830] px-8 py-12 text-white sm:px-12 lg:px-16">
            <div className="mx-auto w-full max-w-[432px]">
              <button
                type="button"
                onClick={() => navigate('/')}
                className="inline-flex items-center gap-2 rounded-md text-left transition-opacity hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-white/60 focus:ring-offset-2 focus:ring-offset-[#101830]"
              >
                <span className="h-3 w-3 rounded-full bg-[#1abf7d]" />
                <span className="text-[24px] font-bold tracking-[-0.03em]">Sela</span>
              </button>

              <div className="mt-20">
                <h1 className="max-w-[420px] text-[42px] font-bold leading-[1.18] tracking-[-0.05em] text-white">
                  The operating system for real estate predevelopment.
                </h1>
                <p className="mt-8 max-w-[420px] text-[17px] leading-8 text-[#99abc7]">
                  {isPendingWorkspaceCreate
                    ? 'Your parcel evaluation choices are saved. Create an account to finish setting up your workspace.'
                    : 'Coordinate every person, decision, and discipline - from first look to shovel in the ground.'}
                </p>
              </div>

              {/* <div className="mt-16 rounded-2xl bg-[rgba(255,255,255,0.07)] px-6 py-5">
                <p className="text-[15px] leading-7 text-[#d9e0ed]">
                  &quot;We caught a $140k coordination error before it hit the field. That never would&apos;ve happened without
                  Sela.&quot;
                </p>
                <p className="mt-4 text-xs font-medium uppercase tracking-[0.12em] text-[#8099b8]">
                  Marcus T. - Principal, Kamiak Development
                </p>
              </div> */}
            </div>
          </aside>

          <main className="flex items-center justify-center px-6 py-12 sm:px-10 lg:px-16">
            <div className="w-full max-w-[400px]">
              {!requiresConfirmation ? (
                <>
                  <h2 className="text-[40px] font-bold tracking-[-0.04em] text-[#101830]">Create your account</h2>
                  <p className="mt-3 text-[15px] text-[#708099]">
                    {isPendingWorkspaceCreate
                      ? 'Create an account to save your evaluation workspace and launch parcel research.'
                      : 'Get your first project running in under 5 minutes.'}
                  </p>

                  <button
                    type="button"
                    onClick={handleGoogleSignUp}
                    disabled={isSubmitting || isGoogleLoading}
                    className="mt-8 flex h-11 w-full items-center justify-center gap-3 rounded-lg border border-[#d1d9e3] bg-white px-4 text-sm font-medium text-[#26334d] transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-[#101830] focus:ring-offset-2"
                  >
                    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#ea4335] text-[10px] font-bold text-white">
                      G
                    </span>
                    <span>{isGoogleLoading ? 'Redirecting to Google...' : 'Continue with Google'}</span>
                  </button>

                  <div className="my-6 flex items-center gap-3">
                    <div className="h-px flex-1 bg-[#e4eaf1]" />
                    <span className="text-sm text-[#99a6b8]">or</span>
                    <div className="h-px flex-1 bg-[#e4eaf1]" />
                  </div>

                  <form onSubmit={handleSignUp} className="space-y-4">
                    <div>
                      <label htmlFor="signup-full-name" className="mb-2 block text-[13px] font-medium text-[#26334d]">
                        Full name
                      </label>
                      <input
                        id="signup-full-name"
                        type="text"
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        className="h-11 w-full rounded-lg border border-[#d1d9e3] bg-[#f7fafc] px-3 text-sm text-[#26334d] placeholder:text-[#9eadc2] focus:border-[#101830] focus:outline-none focus:ring-2 focus:ring-[#101830]/10"
                        placeholder="Sarah Johnson"
                        required
                        disabled={isSubmitting || isGoogleLoading}
                      />
                    </div>

                    <div>
                      <label htmlFor="signup-email" className="mb-2 block text-[13px] font-medium text-[#26334d]">
                        Work email
                      </label>
                      <input
                        id="signup-email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="h-11 w-full rounded-lg border border-[#d1d9e3] bg-[#f7fafc] px-3 text-sm text-[#26334d] placeholder:text-[#9eadc2] focus:border-[#101830] focus:outline-none focus:ring-2 focus:ring-[#101830]/10"
                        placeholder="sarah@example.com"
                        required
                        disabled={isSubmitting || isGoogleLoading}
                      />
                    </div>

                    <div>
                      <label htmlFor="signup-password" className="mb-2 block text-[13px] font-medium text-[#26334d]">
                        Password
                      </label>
                      <input
                        id="signup-password"
                        type="password"
                        value={password}
                        onChange={(e) => {
                          setPassword(e.target.value);
                          setConfirmPassword(e.target.value);
                        }}
                        className="h-11 w-full rounded-lg border border-[#d1d9e3] bg-[#f7fafc] px-3 text-sm text-[#26334d] placeholder:text-[#9eadc2] focus:border-[#101830] focus:outline-none focus:ring-2 focus:ring-[#101830]/10"
                        placeholder="Create a secure password"
                        required
                        disabled={isSubmitting || isGoogleLoading}
                      />
                    </div>

                    {error ? (
                      <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{error}</div>
                    ) : null}
                    {showPasswordPolicyHint ? (
                      <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                        Use at least 8 characters with uppercase, lowercase, a number, and a special character.
                      </div>
                    ) : null}
                    {info ? (
                      <div className="rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">{info}</div>
                    ) : null}

                    <button
                      type="submit"
                      disabled={isSubmitting || isGoogleLoading || !fullName.trim() || !email.trim() || !password.trim()}
                      className="mt-2 flex h-12 w-full items-center justify-center rounded-lg bg-[#101830] px-4 text-[15px] font-semibold text-white transition-colors hover:bg-[#18233f] disabled:cursor-not-allowed disabled:bg-[#aeb8c8] focus:outline-none focus:ring-2 focus:ring-[#101830] focus:ring-offset-2"
                    >
                      {isSubmitting ? 'Creating account...' : 'Create free account ->'}
                    </button>
                  </form>

                  <div className="mt-6 text-center text-[13px]">
                    <span className="text-[#808fa6]">Already have an account? </span>
                    <button
                      type="button"
                      onClick={() => {
                        recordUserAction('signup_to_login_click');
                        navigate(`/login${nextSearch}`);
                      }}
                      className="font-medium text-[#101830] underline decoration-[#101830] underline-offset-2"
                      disabled={isSubmitting}
                    >
                      Sign in
                    </button>
                  </div>

                  <p className="mt-6 text-center text-[11px] leading-5 text-[#a6b2c7]">
                    By creating an account you agree to our Terms of Service and Privacy Policy.
                  </p>
                </>
              ) : (
                <>
                  <h2 className="text-[40px] font-bold tracking-[-0.04em] text-[#101830]">Verify your email</h2>
                  <p className="mt-3 text-[15px] text-[#708099]">Enter the verification code we sent to complete your account.</p>

                  <form onSubmit={handleConfirm} className="mt-8 space-y-4">
                    <div>
                      <label htmlFor="confirmation-code" className="mb-2 block text-[13px] font-medium text-[#26334d]">
                        Verification code
                      </label>
                      <input
                        id="confirmation-code"
                        type="text"
                        value={confirmationCode}
                        onChange={(e) => setConfirmationCode(e.target.value)}
                        className="h-11 w-full rounded-lg border border-[#d1d9e3] bg-[#f7fafc] px-3 text-sm text-[#26334d] placeholder:text-[#9eadc2] focus:border-[#101830] focus:outline-none focus:ring-2 focus:ring-[#101830]/10"
                        placeholder="Enter code from email"
                        required
                        disabled={isSubmitting}
                      />
                    </div>

                    {error ? (
                      <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{error}</div>
                    ) : null}
                    {info ? (
                      <div className="rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">{info}</div>
                    ) : null}

                    <button
                      type="submit"
                      disabled={isSubmitting || !confirmationCode.trim()}
                      className="flex h-12 w-full items-center justify-center rounded-lg bg-[#101830] px-4 text-[15px] font-semibold text-white transition-colors hover:bg-[#18233f] disabled:cursor-not-allowed disabled:bg-[#aeb8c8] focus:outline-none focus:ring-2 focus:ring-[#101830] focus:ring-offset-2"
                    >
                      {isSubmitting ? 'Verifying...' : 'Verify email ->'}
                    </button>
                  </form>
                </>
              )}
            </div>
          </main>
        </div>
      </div>
  );
};

export default SignUp;
